class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector('#predictive-search');
    this.announcer = this.querySelector('#predictive-search-announcer');
    this.categorySelect = this.querySelector('[data-category-select]');
    this.form = this.querySelector('form');
    this.minChars = 2;
    this.limit = 8;
    this.controller = null;
    this.activeIndex = -1;

    if (this.form) {
      this.form.addEventListener('submit', (event) => {
        const option = this.categorySelect && this.categorySelect.selectedOptions[0];
        const collectionUrl = option && option.dataset.collectionUrl;
        if (!collectionUrl) return;
        event.preventDefault();
        const term = this.input.value.trim();
        window.location.href = collectionUrl + (term ? '?q=' + encodeURIComponent(term) : '');
      });
    }

    this.input.addEventListener(
      'input',
      this.debounce((event) => {
        this.onChange(event);
      }, 300).bind(this)
    );

    this.input.addEventListener('focus', () => {
      if (this.input.value.trim().length >= this.minChars && this.predictiveSearchResults.innerHTML.trim()) {
        this.open();
      }
    });

    document.addEventListener('click', (event) => {
      if (!this.contains(event.target)) this.close();
    });

    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        this.close();
        this.input.blur();
        return;
      }

      if (!this.hasAttribute('search-open')) return;

      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.moveActive(1);
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.moveActive(-1);
      } else if (event.key === 'Enter' && this.activeIndex > -1) {
        const active = this.getOptions()[this.activeIndex];
        const link = active && active.querySelector('a[href]');
        if (link) {
          event.preventDefault();
          window.location.href = link.href;
        }
      }
    });
  }

  getOptions() {
    return Array.from(this.predictiveSearchResults.querySelectorAll('[role="option"]'));
  }

  moveActive(direction) {
    const options = this.getOptions();
    if (!options.length) return;

    if (this.activeIndex > -1) options[this.activeIndex].classList.remove('is-active');

    this.activeIndex += direction;
    if (this.activeIndex < -1) this.activeIndex = options.length - 1;
    if (this.activeIndex >= options.length) this.activeIndex = -1;

    if (this.activeIndex === -1) {
      this.input.removeAttribute('aria-activedescendant');
      return;
    }

    const active = options[this.activeIndex];
    active.classList.add('is-active');
    active.scrollIntoView({ block: 'nearest' });
    this.input.setAttribute('aria-activedescendant', active.id);
  }

  onChange() {
    const searchTerm = this.input.value.trim();

    if (searchTerm.length < this.minChars) {
      this.close();
      return;
    }

    this.getSearchResults(searchTerm);
  }

  getSearchResults(searchTerm) {
    if (this.controller) this.controller.abort();
    this.controller = new AbortController();

    fetch(
      `/search/suggest?q=${encodeURIComponent(searchTerm)}&resources[type]=product&resources[limit]=${this.limit}&resources[options][unavailable_products]=last&section_id=predictive-search`,
      { signal: this.controller.signal }
    )
      .then((response) => {
        if (!response.ok) {
          const error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        const resultsMarkup = doc ? doc.innerHTML : '';
        this.predictiveSearchResults.innerHTML = resultsMarkup;
        this.activeIndex = -1;
        this.input.removeAttribute('aria-activedescendant');

        const resultCount = this.getOptions().length;
        if (this.announcer) {
          this.announcer.textContent = resultCount
            ? resultCount + ' result' + (resultCount === 1 ? '' : 's') + ' found'
            : 'No results found';
        }

        if (resultsMarkup.trim()) {
          this.open();
          if (window.setupButtons) {
            window.setupButtons(this.predictiveSearchResults.querySelectorAll('[button-wishlist]'));
          }
        } else {
          this.close();
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        this.close();
      });
  }

  open() {
    this.predictiveSearchResults.style.display = 'block';
    this.setAttribute('search-open', '');
    this.input.setAttribute('aria-expanded', 'true');
  }

  close() {
    this.predictiveSearchResults.style.display = 'none';
    this.removeAttribute('search-open');
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
    this.activeIndex = -1;
  }

  debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

if (!customElements.get('predictive-search')) {
  customElements.define('predictive-search', PredictiveSearch);
}
